import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const checklistItems = [
  'checklist_item_1',
  'checklist_item_2',
  'checklist_item_3',
  'checklist_item_4',
  'checklist_item_5',
  'checklist_item_6',
];

export default function Checklist() {
  const { t } = useTranslation('refection');
  const { ref, isVisible } = useScrollReveal<HTMLElement>(0.1);
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const checkedCount = Object.values(checked).filter(Boolean).length;

  const toggleCheck = (idx: number) => {
    setChecked((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={ref} id="checklist" className="bg-white py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-start">
          {/* Left: Text + Image */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl mb-4 md:mb-6 leading-tight">
              {t('checklist_h2')}
            </h2>
            <p className="text-slate-primary/70 text-base md:text-lg leading-relaxed mb-8">
              {t('checklist_intro')}
            </p>

            {/* Image */}
            <div className="rounded-xl overflow-hidden mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Old%20worn%20asphalt%20shingle%20roof%20on%20a%20Quebec%20residential%20house%20showing%20clear%20signs%20of%20aging%2C%20curled%20and%20cracked%20shingles%2C%20moss%20growth%2C%20damaged%20roof%20surface%20in%20need%20of%20complete%20refection%2C%20dramatic%20overcast%20sky%2C%20professional%20inspection%20photography%2C%20no%20text%20visible&width=600&height=400&seq=ref-check1&orientation=landscape"
                alt="Toiture endommagée nécessitant une réfection complète à Lévis — inspection par couvreur RBQ"
                className="w-full h-56 md:h-64 object-cover object-top"
                loading="lazy"
                width="600"
                height="400"
                width="600"
                height="400"
              />
            </div>

            {/* Encadré */}
            <div className="bg-copper/10 border-l-4 border-copper rounded-r-lg p-5 md:p-6">
              <p className="text-slate-primary font-semibold text-sm md:text-base mb-3">
                {t('checklist_box')}
              </p>
              <button
                onClick={() => scrollToSection('quote-form')}
                className="inline-flex items-center gap-2 bg-copper hover:bg-copper-dark text-white font-semibold px-5 py-2.5 rounded-md text-sm transition-all duration-300 whitespace-nowrap"
              >
                {t('checklist_cta')}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>

          {/* Checklist visuelle */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="space-y-3">
              {checklistItems.map((key, idx) => (
                <button
                  key={idx}
                  onClick={() => toggleCheck(idx)}
                  className={`w-full flex items-start gap-4 p-4 md:p-5 rounded-lg border-2 transition-all duration-300 text-left ${
                    checked[idx]
                      ? 'border-copper bg-copper/5'
                      : 'border-soft-dark bg-soft-gray hover:border-copper/40'
                  }`}
                >
                  <span
                    className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded border-2 transition-all duration-300 mt-0.5 ${
                      checked[idx]
                        ? 'border-copper bg-copper text-white'
                        : 'border-slate-primary/30'
                    }`}
                  >
                    {checked[idx] && <i className="ri-check-line text-sm" />}
                  </span>
                  <span
                    className={`text-sm md:text-base transition-all duration-300 ${
                      checked[idx]
                        ? 'text-slate-primary font-medium'
                        : 'text-slate-primary/70'
                    }`}
                  >
                    {t(key)}
                  </span>
                </button>
              ))}
            </div>

            {/* Compteur */}
            <div className="mt-4 text-center">
              <span className="text-sm text-slate-primary/60">
                {checkedCount} / {checklistItems.length} signes cochés
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}